'use client';

import { useState } from 'react';
import { Loader2, AlertCircle, CreditCard, ChevronLeft, Clock } from 'lucide-react';

/* ── Types ───────────────────────────────────────────────────────────────────── */

export type PaymentStatus = 'loading' | 'simulate' | 'redirecting' | 'error' | 'late-pending';

interface StepPaymentProps {
  status: PaymentStatus;
  errorMsg: string | null;
  errorCode: string | null;
  bookingId: string | null;
  onSimulatePay: (outcome: 'paid' | 'failed') => void;
  onBack: () => void;
}

/* ── Component ───────────────────────────────────────────────────────────────── */

export default function StepPayment({
  status,
  errorMsg,
  errorCode,
  onSimulatePay,
  onBack,
}: StepPaymentProps) {
  const [simulateOutcome, setSimulateOutcome] = useState<'paid' | 'failed'>('paid');

  /* ── Loading / redirecting ─────────────────────────────────────────────── */

  if (status === 'loading' || status === 'redirecting') {
    return (
      <div data-cid="wizard-pay" className="flex flex-col items-center gap-5 py-12">
        <Loader2 size={40} className="text-sky-500 animate-spin" />
        <p className="text-sm font-semibold text-slate-600">
          {status === 'loading' ? 'Creo la prenotazione…' : 'Redirezione al pagamento…'}
        </p>
      </div>
    );
  }

  /* ── Error ─────────────────────────────────────────────────────────────── */

  if (status === 'error') {
    const isKyc =
      errorCode === 'KYC_PHONE_REQUIRED' || errorCode === 'KYC_DOC_REQUIRED';

    return (
      <div data-cid="wizard-pay-error" className="flex flex-col gap-5 py-4">
        <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <AlertCircle size={18} className="text-rose-500 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-800 leading-snug">
            {errorMsg ?? 'Si è verificato un errore. Riprova.'}
          </p>
        </div>

        {isKyc && (
          <a
            href="/profile/kyc"
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold rounded-full bg-sky-600 text-white hover:bg-sky-700 transition-all"
          >
            Completa verifica KYC
          </a>
        )}

        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-sky-600 transition-colors"
        >
          <ChevronLeft size={15} />
          Torna ai dati
        </button>
      </div>
    );
  }

  /* ── Prenotazione tardiva: in attesa approvazione operatore (Fase E cutoff) ── */

  if (status === 'late-pending') {
    return (
      <div data-cid="wizard-pay-late" className="flex flex-col items-center gap-4 py-10 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
          <Clock size={28} className="text-amber-600" />
        </div>
        <p className="text-base font-bold text-slate-800">Richiesta inviata</p>
        <p className="text-sm text-slate-600 leading-relaxed max-w-sm">
          La tua è una <strong>prenotazione last-minute</strong> (oltre l&apos;orario limite):
          deve essere <strong>confermata dall&apos;operatore</strong>. Non è stato addebitato nulla.
          Riceverai una email con il <strong>link per pagare</strong> appena viene approvata —
          la trovi anche nel tuo profilo, sezione prenotazioni, col tasto <strong>&quot;Paga ora&quot;</strong>.
        </p>
        <a href="/profile" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold rounded-full bg-sky-600 text-white hover:bg-sky-700 transition-all">
          Vai alle mie prenotazioni
        </a>
      </div>
    );
  }

  /* ── Simulate (staging: MOLLIE_FORCE_SIMULATE=true) ────────────────────── */

  return (
    <div data-cid="wizard-pay-simulate" className="flex flex-col gap-5 py-4">
      <div className="flex items-center gap-2.5">
        <CreditCard size={20} className="text-sky-600 shrink-0" />
        <p className="text-base font-bold text-slate-800">Pagamento (test simulato)</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-xs text-amber-800 leading-relaxed">
          <strong>Ambiente staging</strong> — nessun addebito reale.
          Scegli l&apos;esito e premi il bottone per completare il flusso.
        </p>
      </div>

      {/* Toggle esito paid / failed */}
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 shrink-0">
          Esito simulato
        </span>
        <div className="flex rounded-full border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setSimulateOutcome('paid')}
            className={[
              'px-4 py-1.5 text-xs font-bold transition-colors',
              simulateOutcome === 'paid'
                ? 'bg-emerald-500 text-white'
                : 'text-slate-500 hover:bg-slate-50',
            ].join(' ')}
          >
            Pagato
          </button>
          <button
            type="button"
            onClick={() => setSimulateOutcome('failed')}
            className={[
              'px-4 py-1.5 text-xs font-bold transition-colors',
              simulateOutcome === 'failed'
                ? 'bg-rose-500 text-white'
                : 'text-slate-500 hover:bg-slate-50',
            ].join(' ')}
          >
            Fallito
          </button>
        </div>
      </div>

      <button
        type="button"
        data-cid="wizard-pay-simulate-btn"
        onClick={() => onSimulatePay(simulateOutcome)}
        className="w-full px-6 py-3 text-sm font-bold rounded-full bg-sky-600 text-white hover:bg-sky-700 shadow-md transition-all"
      >
        Paga (simulato)
      </button>

      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors"
      >
        <ChevronLeft size={13} />
        Torna ai dati
      </button>
    </div>
  );
}
